import React, { useMemo } from "react";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

dayjs.locale("fr");

const periodOptions = [
  { value: "30", label: "30 j" },
  { value: "90", label: "90 j" },
  { value: "365", label: "365 j" },
  { value: "all", label: "Tout" },
];

const AttendanceChart = ({ data = [], loading, period, setPeriod }) => {
  const chartData = useMemo(() => {
    const safeData = Array.isArray(data) ? data : [];

    let mapped = safeData
      .map((item) => {
        const date = dayjs(item?.date_presence);
        return {
          rawDate: item?.date_presence,
          timestamp: date.isValid() ? date.valueOf() : 0,
          dateLabel: date.isValid() ? date.format("DD/MM") : "—",
          fullDateLabel: date.isValid() ? date.format("DD/MM/YYYY") : "—",
          nombre_appels: Number(item?.nombre_appels || 0),
        };
      })
      .sort((a, b) => a.timestamp - b.timestamp);

    if (period !== "all") {
      const days = Number(period);
      const minDate = dayjs().startOf("day").subtract(days, "day");
      mapped = mapped.filter((item) =>
        dayjs(item.rawDate).isAfter(minDate)
      );
    }

    return mapped;
  }, [data, period]);

  const totalCalls = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.nombre_appels, 0);
  }, [chartData]);

  const averageCalls = useMemo(() => {
    if (!chartData.length) return 0;
    return Math.round((totalCalls / chartData.length) * 10) / 10;
  }, [chartData, totalCalls]);

  return (
    <div className="rounded-[28px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4 md:p-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
            Évolution des appels de présence
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Visualise le rythme global des appels pour détecter les périodes
            de baisse d’activité.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {periodOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setPeriod(option.value)}
              className={`rounded-2xl px-3 py-2 text-sm border transition ${
                period === option.value
                  ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-800"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">
            Période affichée
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
            {period === "all" ? "Complète" : `${period} jours`}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">
            Appels cumulés
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
            {totalCalls}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">
            Moyenne par jour
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
            {averageCalls}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="h-[380px] rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
      ) : chartData.length === 0 ? (
        <div className="h-[260px] flex items-center justify-center rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400">
          Aucune donnée pour cette période.
        </div>
      ) : (
        <div style={{ position: "relative", zIndex: 0 }} className="h-[380px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="attendanceFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.03} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" />
              <XAxis
                dataKey="dateLabel"
                tick={{ fill: "#64748b", fontSize: 12 }}
              />
              <YAxis tick={{ fill: "#64748b", fontSize: 12 }} />
              <Tooltip
                formatter={(value) => [`${value} appel(s)`, "Appels"]}
                labelFormatter={(label, payload) =>
                  payload?.[0]?.payload?.fullDateLabel || label
                }
                contentStyle={{
                  borderRadius: 16,
                  border: "1px solid #e2e8f0",
                  background: "#0f172a",
                  color: "#fff",
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="nombre_appels"
                name="Nombre d'appels"
                stroke="#0ea5e9"
                strokeWidth={3}
                fill="url(#attendanceFill)"
                activeDot={{ r: 5 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default AttendanceChart;